import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { useApi } from '../apiContext';
import { env } from '../config';
import { ApiError } from '../api';
import { buildExtensions } from '../editor/extensions';
import { editorMarkdown, normalizeMarkdown } from '../editor/markdown';
import { applyMerge, threeWayMerge, type MergeHunk } from '../editor/merge';
import type { EditorContext, QueryInfo, WikiPageContent } from '../types';
import MergeDialog from './MergeDialog';
import QueryPicker from './QueryPicker';
import Toolbar from './Toolbar';

interface Props {
  project: string;
  wikiId: string;
  path: string;
  isNew?: boolean;
  onSaved?: () => void;
}

type Status = { kind: 'idle' | 'saving' | 'saved' | 'error' | 'info'; text: string };

const POLL_MS = 30_000;

export default function WikiEditor({ project, wikiId, path, isNew, onSaved }: Props) {
  const api = useApi();
  const ctx = useMemo<EditorContext>(() => ({ project, wiki: wikiId, adoOrg: env.adoOrg, api }), [project, wikiId, api]);
  const extensions = useMemo(() => buildExtensions(ctx), [ctx]);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [status, setStatus] = useState<Status>({ kind: 'idle', text: '' });
  const [remoteChanged, setRemoteChanged] = useState(false);
  const [merge, setMerge] = useState<{ hunks: MergeHunk[]; latest: WikiPageContent; theirs: string } | null>(null);
  const [queryPicker, setQueryPicker] = useState(false);
  const [sourceMode, setSourceMode] = useState(false);
  const [source, setSource] = useState('');

  // Mutable save state: the version we loaded and the normalized markdown it had.
  const etagRef = useRef<string | undefined>(undefined);
  const baseRef = useRef('');
  const savingRef = useRef(false);
  const dirtyTimer = useRef<number | null>(null);

  const editor = useEditor({
    extensions,
    content: '',
    contentType: 'markdown',
    editorProps: { attributes: { class: 'wiki-content', spellcheck: 'true' } },
    onUpdate: ({ editor }) => {
      if (dirtyTimer.current) window.clearTimeout(dirtyTimer.current);
      dirtyTimer.current = window.setTimeout(() => setDirty(editorMarkdown(editor) !== baseRef.current), 250);
    },
  }, [extensions]);

  // ---- load ----
  const load = useCallback(async () => {
    if (!editor) return;
    setLoading(true);
    setLoadError(null);
    setRemoteChanged(false);
    setMerge(null);
    try {
      if (isNew) {
        editor.commands.setContent(`# ${path.split('/').pop()}\n`, { contentType: 'markdown', emitUpdate: false });
        etagRef.current = undefined;
        baseRef.current = '';
        setDirty(true);
      } else {
        const page = await api.getPage(project, wikiId, path);
        editor.commands.setContent(page.content, { contentType: 'markdown', emitUpdate: false });
        etagRef.current = page.etag;
        baseRef.current = editorMarkdown(editor);
        setDirty(false);
      }
      setStatus({ kind: 'idle', text: '' });
    } catch (e) {
      setLoadError(e instanceof ApiError ? `${e.code}${e.detail ? `: ${e.detail}` : ''}` : String(e));
    } finally {
      setLoading(false);
    }
  }, [api, editor, project, wikiId, path, isNew]);

  useEffect(() => {
    void load();
  }, [load]);

  // ---- remote change polling ----
  useEffect(() => {
    if (isNew || loading) return;
    const id = window.setInterval(async () => {
      if (savingRef.current || !etagRef.current) return;
      try {
        const { etag } = await api.getPageEtag(project, wikiId, path);
        if (etag && etag !== etagRef.current) setRemoteChanged(true);
      } catch {
        /* transient */
      }
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [api, project, wikiId, path, isNew, loading]);

  // ---- unsaved changes guard ----
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirty) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // ---- save & merge ----
  const currentMarkdown = useCallback(() => {
    if (!editor) return '';
    if (sourceMode) return normalizeMarkdown(source);
    return editorMarkdown(editor);
  }, [editor, sourceMode, source]);

  const commitToEditor = useCallback(
    (markdown: string) => {
      if (!editor) return;
      editor.commands.setContent(markdown, { contentType: 'markdown', emitUpdate: false });
      if (sourceMode) setSource(markdown);
    },
    [editor, sourceMode],
  );

  const save = useCallback(
    async (overrideContent?: string) => {
      if (!editor || savingRef.current) return;
      const mine = overrideContent ?? currentMarkdown();
      savingRef.current = true;
      setStatus({ kind: 'saving', text: 'Saving…' });
      try {
        const res = await api.savePage(project, wikiId, path, mine, etagRef.current);
        if (res.ok) {
          etagRef.current = res.etag;
          baseRef.current = mine;
          setDirty(false);
          setRemoteChanged(false);
          setStatus({ kind: 'saved', text: `Saved ${new Date().toLocaleTimeString()}` });
          onSaved?.();
          return;
        }
        // Conflict: three-way merge against what we loaded.
        const theirs = normalizeMarkdown(res.latest.content);
        const outcome = threeWayMerge(baseRef.current, mine, theirs);
        if (outcome.conflicts === 0) {
          const merged = applyMerge(outcome.hunks);
          commitToEditor(merged);
          etagRef.current = res.latest.etag;
          baseRef.current = theirs;
          savingRef.current = false;
          setStatus({ kind: 'info', text: 'Merged changes from another editor…' });
          await save(merged);
          setStatus({ kind: 'saved', text: `Merged with another editor's changes and saved ${new Date().toLocaleTimeString()}` });
          return;
        }
        setMerge({ hunks: outcome.hunks, latest: res.latest, theirs });
        setStatus({ kind: 'info', text: 'Conflicting changes — resolve to save.' });
      } catch (e) {
        const msg = e instanceof ApiError ? (e.code === 'ado_forbidden' ? 'You do not have permission to edit this wiki.' : `${e.code}${e.detail ? `: ${e.detail}` : ''}`) : String(e);
        setStatus({ kind: 'error', text: `Save failed: ${msg}` });
      } finally {
        savingRef.current = false;
      }
    },
    [api, editor, project, wikiId, path, currentMarkdown, commitToEditor, onSaved],
  );

  const applyMergeAndSave = async (hunks: MergeHunk[]) => {
    if (!merge) return;
    const merged = applyMerge(hunks);
    commitToEditor(merged);
    etagRef.current = merge.latest.etag;
    baseRef.current = merge.theirs;
    setMerge(null);
    await save(merged);
  };

  const useTheirs = () => {
    if (!merge) return;
    commitToEditor(merge.theirs);
    etagRef.current = merge.latest.etag;
    baseRef.current = merge.theirs;
    setMerge(null);
    setDirty(false);
    setRemoteChanged(false);
    setStatus({ kind: 'info', text: 'Loaded the latest version; your changes were discarded.' });
  };

  /** "Merge now" from the banner: pull the latest and merge without saving. */
  const mergeRemote = async () => {
    if (!editor) return;
    try {
      const latest = await api.getPage(project, wikiId, path);
      const theirs = normalizeMarkdown(latest.content);
      const mine = currentMarkdown();
      if (!dirty) {
        commitToEditor(theirs);
        etagRef.current = latest.etag;
        baseRef.current = theirs;
        setRemoteChanged(false);
        setStatus({ kind: 'info', text: 'Reloaded the latest version.' });
        return;
      }
      const outcome = threeWayMerge(baseRef.current, mine, theirs);
      if (outcome.conflicts === 0) {
        commitToEditor(applyMerge(outcome.hunks));
        etagRef.current = latest.etag;
        baseRef.current = theirs;
        setRemoteChanged(false);
        setDirty(true);
        setStatus({ kind: 'info', text: 'Merged the other editor’s changes into your draft. Save when ready.' });
      } else {
        setMerge({ hunks: outcome.hunks, latest, theirs });
      }
    } catch (e) {
      setStatus({ kind: 'error', text: `Could not fetch the latest version: ${String(e)}` });
    }
  };

  // Ctrl/Cmd+S
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (dirty) void save();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [dirty, save]);

  // ---- source mode ----
  const toggleSource = () => {
    if (!editor) return;
    if (!sourceMode) {
      setSource(editorMarkdown(editor));
      setSourceMode(true);
    } else {
      editor.commands.setContent(source, { contentType: 'markdown', emitUpdate: false });
      setSourceMode(false);
      setDirty(editorMarkdown(editor) !== baseRef.current);
    }
  };

  const insertQuery = (q: QueryInfo) => {
    editor?.chain().focus().insertContent({ type: 'adoQuery', attrs: { queryId: q.id } }).run();
    setQueryPicker(false);
  };

  if (!editor) return null;

  const adoPageUrl = `https://dev.azure.com/${env.adoOrg}/${encodeURIComponent(project)}/_wiki/wikis/${encodeURIComponent(wikiId)}?pagePath=${encodeURIComponent(path)}`;

  return (
    <section className="editor-pane">
      <div className="editor-head">
        <div className="editor-path" title={path}>
          {path.split('/').filter(Boolean).map((seg, i, arr) => (
            <span key={i}>
              {i > 0 && <span className="crumb-sep">/</span>}
              <span className={i === arr.length - 1 ? 'crumb crumb-current' : 'crumb'}>{seg}</span>
            </span>
          ))}
          {isNew && <span className="badge">new</span>}
        </div>
        <span className={`status status-${status.kind}`}>{status.text || (dirty ? 'Unsaved changes' : loading ? 'Loading…' : '')}</span>
        <a className="btn btn-ghost btn-sm" href={adoPageUrl} target="_blank" rel="noreferrer">
          Open in ADO
        </a>
        <button className="btn btn-primary" onClick={() => void save()} disabled={!dirty || status.kind === 'saving' || loading}>
          Save
        </button>
      </div>

      {remoteChanged && (
        <div className="notice notice-warn">
          This page was changed in Azure DevOps by someone else.
          <button className="btn btn-sm" onClick={() => void mergeRemote()}>
            {dirty ? 'Merge their changes into my draft' : 'Reload'}
          </button>
        </div>
      )}

      <Toolbar editor={editor} onInsertQuery={() => setQueryPicker(true)} sourceMode={sourceMode} onToggleSource={toggleSource} />

      {loadError && <div className="notice notice-error">Could not load this page: {loadError}</div>}

      <div className="editor-scroll">
        {sourceMode ? (
          <textarea
            className="source-editor"
            value={source}
            spellCheck={false}
            onChange={e => {
              setSource(e.target.value);
              setDirty(true);
            }}
          />
        ) : (
          <EditorContent editor={editor} className={loading ? 'is-loading' : ''} />
        )}
      </div>

      {queryPicker && <QueryPicker api={api} project={project} onPick={insertQuery} onClose={() => setQueryPicker(false)} />}
      {merge && <MergeDialog hunks={merge.hunks} onApply={h => void applyMergeAndSave(h)} onUseTheirs={useTheirs} onCancel={() => setMerge(null)} />}
    </section>
  );
}
